/**
 * @module pilot.service
 */
import { DialVerseApi } from '../app/api'
import AppConfig from '../config'

const PilotService = DialVerseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Basic CRUD operations
    getPilots: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/pilots'),
        method: 'GET',
      }),
    }),
    getPilot: builder.mutation({
      query: (pilotId) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}`),
        method: 'GET',
      }),
    }),
    getPilotByNumber: builder.mutation({
      query: (number) => ({
        url: AppConfig.getUrl(`/pilots/number/${number}`),
        method: 'GET',
      }),
    }),
    createPilot: builder.mutation({
      query: (pilot) => ({
        url: AppConfig.getUrl('/pilots'),
        method: 'POST',
        body: pilot,
      }),
    }),
    updatePilot: builder.mutation({
      query: ({ pilotId, ...updates }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}`),
        method: 'PUT',
        body: updates,
      }),
    }),
    deletePilot: builder.mutation({
      query: (pilotId) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}`),
        method: 'DELETE',
      }),
    }),

    // Status and profile updates
    updatePilotStatus: builder.mutation({
      query: ({ pilotId, status }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/status`),
        method: 'PATCH',
        body: { status },
      }),
    }),
    updateLicense: builder.mutation({
      query: ({ pilotId, license }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/license`),
        method: 'PATCH',
        body: { license },
      }),
    }),
    updateMedical: builder.mutation({
      query: ({ pilotId, medical }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/medical`),
        method: 'PATCH',
        body: { medical },
      }),
    }),
    updateTraining: builder.mutation({
      query: ({ pilotId, training }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/training`),
        method: 'PATCH',
        body: { training },
      }),
    }),
    updateFlightExperience: builder.mutation({
      query: ({ pilotId, flightExperience }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/flight-experience`),
        method: 'PATCH',
        body: { flightExperience },
      }),
    }),
    updateEmergencyContact: builder.mutation({
      query: ({ pilotId, emergencyContact }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/emergency-contact`),
        method: 'PATCH',
        body: { emergencyContact },
      }),
    }),
    updateAddress: builder.mutation({
      query: ({ pilotId, address }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/address`),
        method: 'PATCH',
        body: { address },
      }),
    }),

    // Type rating operations
    addTypeRating: builder.mutation({
      query: ({ pilotId, typeRating }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/type-ratings`),
        method: 'POST',
        body: { typeRating },
      }),
    }),
    removeTypeRating: builder.mutation({
      query: ({ pilotId, rating }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/type-ratings/${rating}`),
        method: 'DELETE',
      }),
    }),

    // Flight hours and ratings
    addFlightHours: builder.mutation({
      query: ({ pilotId, flightHours }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/flight-hours`),
        method: 'POST',
        body: { flightHours },
      }),
    }),
    updateInstrumentRating: builder.mutation({
      query: ({ pilotId, instrumentRating }) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/instrument-rating`),
        method: 'PATCH',
        body: { instrumentRating },
      }),
    }),

    // Employment status
    terminate: builder.mutation({
      query: (pilotId) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/terminate`),
        method: 'POST',
      }),
    }),
    reactivate: builder.mutation({
      query: (pilotId) => ({
        url: AppConfig.getUrl(`/pilots/${pilotId}/reactivate`),
        method: 'POST',
      }),
    }),

    // Airline and aircraft operations
    getPilotsByAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/pilots/airline/${airlineId}`),
        method: 'GET',
      }),
    }),
    getPilotsByAircraft: builder.mutation({
      query: (aircraftId) => ({
        url: AppConfig.getUrl(`/pilots/aircraft/${aircraftId}`),
        method: 'GET',
      }),
    }),

    // Compliance and monitoring
    getPilotsNeedingRecurrentTraining: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/pilots/compliance/recurrent-training'),
        method: 'GET',
      }),
    }),
    getPilotsWithExpiringMedical: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/pilots/compliance/expiring-medical'),
        method: 'GET',
      }),
    }),
    getPilotsWithExpiringLicense: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/pilots/compliance/expiring-license'),
        method: 'GET',
      }),
    }),
    getPilotsWithExpiringSimulatorCheck: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/pilots/compliance/expiring-simulator'),
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPilotsMutation,
  useGetPilotMutation,
  useGetPilotByNumberMutation,
  useCreatePilotMutation,
  useUpdatePilotMutation,
  useDeletePilotMutation,
  useUpdatePilotStatusMutation,
  useUpdateLicenseMutation,
  useUpdateMedicalMutation,
  useUpdateTrainingMutation,
  useUpdateFlightExperienceMutation,
  useUpdateEmergencyContactMutation,
  useUpdateAddressMutation,
  useAddTypeRatingMutation,
  useRemoveTypeRatingMutation,
  useAddFlightHoursMutation,
  useUpdateInstrumentRatingMutation,
  useTerminateMutation,
  useReactivateMutation,
  useGetPilotsByAirlineMutation,
  useGetPilotsByAircraftMutation,
  useGetPilotsNeedingRecurrentTrainingMutation,
  useGetPilotsWithExpiringMedicalMutation,
  useGetPilotsWithExpiringLicenseMutation,
  useGetPilotsWithExpiringSimulatorCheckMutation,
} = PilotService
